import type { UserAddressApiDto } from "@9amhealth/openapi";
import {
  AutoForm,
  AutoFormAutocompleteField,
  AutoFormInputField,
  Button,
  useAutoFormControls
} from "@9amhealth/shared";
import styled from "@emotion/styled";
import type { CSSProperties, FC, ReactNode } from "react";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { Item } from "react-stately";
import { APP_CONTENT_WIDTH } from "src/constants/layout";
import { stateCodeToNameMap } from "src/constants/stateCodeToNameMap";
import { getAvailableStates } from "src/lib/getAvailableStates";
import parseInputData from "src/lib/parseInputData";
import translate from "src/lib/translate";
import ShipmentAddressCubit from "src/state/ShipmentAddressCubit/ShipmentAddressCubit";
import { ShipmentAddressSavingFailureState } from "src/state/ShipmentAddressCubit/ShipmentAddressState";
import { useBloc } from "src/state/state";
import UserPreferencesCubit from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { Pad } from "src/ui/styled/Pad";
import { z } from "zod";
import Loader from "../Loader/Loader";
import ErrorBox from "../StyledComponents/ErrorBox";
import Translate from "../Translate/Translate";
import { useBlocNext } from "@blac/react";
import { ShippingFormAddressSuggestion } from "./ShippingFormAddressSuggestion";
import { ShippingAddressSchema } from "src/state/ShippingAddress/ShippingAddressSchema";
import { ShippingAddressSuggestionCubit } from "src/state/ShippingAddress/ShippingAddressSuggestionCubit";
import { ShippingAddressData } from "src/state/ShippingAddress/ShippingAddressData";
import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";
import { ShippingAddressValidationCubit } from "src/state/ShippingAddress/ShippingAddressValidationCubit";

const Wrapper = styled.div<{ isShippingUpdate?: boolean }>`
  display: flex;
  flex-direction: column;
  row-gap: 1em;
  max-width: var(--section-max-width, 100%);
  margin: 0 auto;
  padding: ${({ isShippingUpdate }): string =>
    isShippingUpdate ? "0" : "0 var(--space-md)"};
  box-sizing: border-box;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1em;

  @media screen and (min-width: 960px) {
    flex-direction: row;
    justify-content: unset;
  }
`;

export type ShippingFormValues = z.infer<typeof ShippingAddressSchema>;

interface Props {
  onSuccess: (data: UserAddressApiDto) => Promise<void> | void;
  shippingBloc?: ShipmentAddressCubit;
  onSubmitStart?: () => void;
  onValidationEnd?: () => void;
  onError?: () => void;
  loadingElement?: ReactNode;
  formClass?: string;
  loading?: boolean;
  isShippingUpdate?: boolean;
}

const ShippingForm: FC<Props> = (props) => {
  const id = useId();
  const SchemaRef = ShippingAddressSchema;
  const [loading, setLoading] = useState(false);
  const [, { checkZipCode }] = useBlocNext(UserPreferencesCubit);
  const [isInitialized, setIsInitialized] = useState(false);
  const [, { updateShippingAddressSuggestion }] = useBlocNext(
    ShippingAddressSuggestionCubit,
    { id }
  );
  const [, { validateShippingAddressWithApi }] = useBlocNext(
    ShippingAddressValidationCubit
  );

  const shippingBloc = useMemo(
    () => props.shippingBloc ?? new ShipmentAddressCubit(),
    [props.shippingBloc]
  );
  const [addressState, { setShipmentAddress, getShipmentAddress }] = useBloc(
    ShipmentAddressCubit,
    {
      create: () => shippingBloc
    }
  );

  const initialAddress = useMemo(() => addressState.address, [addressState]);
  const availableStates = getAvailableStates();

  const autoFormControls = useAutoFormControls({
    schema: SchemaRef,
    initialValue: {
      firstName: initialAddress.firstName,
      lastName: initialAddress.lastName,
      street: initialAddress.street,
      aptSuite: initialAddress.aptSuite,
      city: initialAddress.city,
      state: availableStates.find(
        (state) => state.name === initialAddress.state
      )?.name,
      zip: initialAddress.zip
    },
    onSubmit: (data) => submitAddress(data)
  });

  const onSuggestionSave = useCallback(
    (address: ShippingAddressData) => {
      setLoading(true);
      void setShipmentAddress(address as UserAddressApiDto, async () => {
        await props.onSuccess(address as UserAddressApiDto);
        setLoading(false);
      });
    },
    [props, setShipmentAddress]
  );

  const isLoading = useCallback(
    () => (props.loading ?? false) || loading,
    [loading, props.loading]
  );

  useEffect((): void => {
    setLoading(true);
    void getShipmentAddress().then(() => {
      setLoading(false);
    });
  }, [getShipmentAddress]);

  useEffect(() => {
    if (!isInitialized && !props.loading && initialAddress.zip) {
      setIsInitialized(true);
      autoFormControls.setValue("street", initialAddress.street);
    }
  }, [initialAddress, isInitialized, autoFormControls, props.loading]);

  useEffect(() => {
    if (addressState instanceof ShipmentAddressSavingFailureState) {
      props.onError?.();
    }
  }, [addressState, props]);

  const submitAddress = (rawData: ShippingFormValues): void => {
    autoFormControls.setError("zip", undefined);
    props.onSubmitStart?.();
    const data = cleanFormData(rawData);
    void (async () => {
      BlockingLoadingOverlayController.startLoading({
        bg: "transparent",
        fadeIn: true,
        message: "shippingAddress.verificationZip"
      });
      // First step: zip validation
      const zipData = await checkZipCode(rawData.zip);
      BlockingLoadingOverlayController.endLoading();
      if (!zipData) {
        props.onValidationEnd?.();
        props.onError?.();
        autoFormControls.setError("zip", translate("invalid_zip_code"));
        return;
      }

      // Next step: Full address validation by address api (Google)
      try {
        const { offerSuggestion, suggestion } =
          await validateShippingAddressWithApi({ data });
        if (!offerSuggestion) {
          setLoading(true);
          void setShipmentAddress(
            suggestion.suggestedAddress as UserAddressApiDto,
            async () => {
              void props.onSuccess(
                suggestion.suggestedAddress as UserAddressApiDto
              );
              setLoading(false);
            }
          );
        } else {
          updateShippingAddressSuggestion({
            originalAddress: data,
            suggestion,
            isSuggestionActive: true
          });
        }
      } catch (e) {
        console.error(e);
        setLoading(false);
        return;
      }
    })();
  };

  const cleanFormData = (
    formData?: Partial<ShippingFormValues>
  ): Partial<ShippingFormValues> => {
    const data: Record<string, number | string | null | undefined> =
      formData ?? {};

    for (const key in data) {
      const value = data[key];
      if (typeof value === "string") {
        data[key] = value.trim();

        if (key === "firstName" || key === "lastName") {
          data[key] = parseInputData("name", value);
        }
      }
    }

    return data;
  };

  return (
    <div
      style={
        {
          "--section-max-width": `${APP_CONTENT_WIDTH}px`
        } as CSSProperties
      }
    >
      {isLoading() ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            height: "100%"
          }}
        >
          <Loader active={true}></Loader>
        </div>
      ) : (
        <>
          <AutoForm {...autoFormControls.props}>
            <Wrapper isShippingUpdate={props.isShippingUpdate}>
              <Group style={{ rowGap: "1em" }}>
                <AutoFormInputField
                  style={{ width: "100%" }}
                  name="firstName"
                  label={translate("first_name")}
                  isRequired
                />

                <AutoFormInputField
                  style={{ width: "100%" }}
                  name="lastName"
                  label={translate("last_name")}
                  isRequired
                />
              </Group>

              <AutoFormInputField
                name="street"
                label={translate("street_address")}
                isRequired
              />

              <AutoFormInputField
                name="aptSuite"
                label={translate("aptSuite")}
              />

              <AutoFormInputField
                name="city"
                label={translate("city")}
                isRequired
              />

              <Group>
                <div style={{ width: "100%" }}>
                  <AutoFormAutocompleteField
                    defaultItems={availableStates}
                    defaultInputValue={
                      initialAddress.state
                        ? stateCodeToNameMap[initialAddress.state]
                        : undefined
                    }
                    name="state"
                    label={translate("state")}
                    isRequired={true}
                  >
                    {(item) => <Item key={item.id}>{item.label}</Item>}
                  </AutoFormAutocompleteField>
                </div>

                <AutoFormInputField
                  style={{ width: "100%" }}
                  name="zip"
                  type="num"
                  mask="00000"
                  label={translate("zip")}
                  isRequired
                />
              </Group>
            </Wrapper>

            <nine-spacer></nine-spacer>
            <Pad>
              {addressState instanceof ShipmentAddressSavingFailureState && (
                <>
                  <nine-spacer s="md"></nine-spacer>
                  <ErrorBox data-severity="error">
                    <Translate msg="error_generic" />
                  </ErrorBox>
                </>
              )}
            </Pad>
            <Button
              disabled={isLoading()}
              type="submit"
              theme="sunrise"
              style={{ margin: "auto" }}
            >
              <Translate msg={props.isShippingUpdate ? "save" : "continue"} />
            </Button>
          </AutoForm>
        </>
      )}
      <ShippingFormAddressSuggestion cubitId={id} onSave={onSuggestionSave} />
    </div>
  );
};

export default ShippingForm;
